export type RepresentativeMethod = 'mean' | 'median' | 'minimum' | 'maximum' | 'manual';
export type ValueClass = 'AV' | 'NAV' | 'NAVN';
export type ElementNature = 'manual' | 'machine' | 'walking' | 'waiting';

export interface ObservationStats {
  n: number;
  mean: number | null;
  median: number | null;
  min: number | null;
  max: number | null;
  range: number | null;
  stdev: number | null;
  cv: number | null;
}

export interface ChronoElementMeasure {
  id: string;
  resourceId: string;
  valueClass: ValueClass;
  nature: ElementNature;
  method: RepresentativeMethod;
  manualSeconds: number | null;
  samples: number[];
}

export function taktSeconds(availableSeconds: number | null, demand: number | null): number | null {
  if (availableSeconds == null || demand == null || demand <= 0 || availableSeconds <= 0) return null;
  return availableSeconds / demand;
}

export function observationStats(samples: number[]): ObservationStats {
  const values = samples.filter((value) => Number.isFinite(value));
  const n = values.length;
  if (n === 0) {
    return { n: 0, mean: null, median: null, min: null, max: null, range: null, stdev: null, cv: null };
  }
  const sorted = [...values].sort((left, right) => left - right);
  const sum = values.reduce((total, value) => total + value, 0);
  const mean = sum / n;
  const mid = Math.floor(n / 2);
  const median = n % 2 === 1 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
  const min = sorted[0];
  const max = sorted[n - 1];
  const stdev = n >= 2
    ? Math.sqrt(values.reduce((total, value) => total + (value - mean) ** 2, 0) / (n - 1))
    : null;
  return {
    n,
    mean,
    median,
    min,
    max,
    range: max - min,
    stdev,
    cv: stdev != null && mean > 0 ? stdev / mean : null,
  };
}

export function representativeSeconds(
  method: RepresentativeMethod,
  manualSeconds: number | null,
  stats: ObservationStats,
): number | null {
  if (method === 'manual') return manualSeconds;
  if (method === 'mean') return stats.mean;
  if (method === 'median') return stats.median;
  if (method === 'minimum') return stats.min;
  return stats.max;
}

export interface QuickAnalysis {
  total: number | null;
  av: number;
  navn: number;
  nav: number;
  avPercent: number | null;
  manual: number;
  machine: number;
  walking: number;
  waiting: number;
  takt: number | null;
  elementsAboveTakt: string[];
  resourcesAboveTakt: string[];
}

export function quickAnalysis(
  elements: ChronoElementMeasure[],
  takt: number | null,
): QuickAnalysis {
  const times = elements.map((element) => representativeSeconds(
    element.method,
    element.manualSeconds,
    observationStats(element.samples),
  ));
  const known = times.filter((value): value is number => value != null);
  const complete = times.length > 0 && known.length === times.length;
  const total = known.length > 0 ? known.reduce((sum, value) => sum + value, 0) : null;
  const sumOf = (pick: (element: ChronoElementMeasure, seconds: number) => boolean) => times.reduce(
    (sum, seconds, index) => (seconds != null && pick(elements[index], seconds) ? sum + seconds : sum),
    0,
  );
  const av = sumOf((element) => element.valueClass === 'AV');
  const byResource = new Map<string, number>();
  elements.forEach((element, index) => {
    const seconds = times[index];
    if (seconds == null) return;
    byResource.set(element.resourceId, (byResource.get(element.resourceId) ?? 0) + seconds);
  });
  return {
    total,
    av,
    navn: sumOf((element) => element.valueClass === 'NAVN'),
    nav: sumOf((element) => element.valueClass === 'NAV'),
    avPercent: complete && total != null && total > 0 ? (av / total) * 100 : null,
    manual: sumOf((element) => element.nature === 'manual'),
    machine: sumOf((element) => element.nature === 'machine'),
    walking: sumOf((element) => element.nature === 'walking'),
    waiting: sumOf((element) => element.nature === 'waiting'),
    takt,
    elementsAboveTakt: elements
      .filter((element, index) => takt != null && times[index] != null && times[index]! > takt)
      .map((element) => element.id),
    resourcesAboveTakt: [...byResource.entries()]
      .filter(([, seconds]) => takt != null && seconds > takt)
      .map(([resourceId]) => resourceId),
  };
}

export function formatChronoNumber(value: number | null, digits = 3): string {
  if (value == null) return '—';
  return new Intl.NumberFormat('pt-BR', { maximumFractionDigits: digits, minimumFractionDigits: 0 }).format(value);
}
